// Pure, dependency-light helpers shared by every checklist-comments
// component (ChecklistComments, ChecklistCommentItem,
// ChecklistCommentComposer) so validation, ownership, and error-message
// rules cannot drift across three files (TRD §2.2.1, CLCMT-F001).
//
// Pure module: no JSX. `getCurrentUserId` is the one function that touches
// `localStorage`, and it is wrapped so it can never throw.

import { NOTE_MAX_LENGTH } from "./checklistStatus";

// Comments share the same length ceiling as status-change notes (OQ7) so
// users learn one rule, not two.
const COMMENT_MAX_LENGTH = NOTE_MAX_LENGTH;
const COMMENT_MIN_TRIMMED_LENGTH = 1;

/**
 * Validates comment text against the length and non-blank rules (AC-F2.2,
 * AC-F2.3, AC-F6.7). Returns the original `text` unmodified as `value` —
 * this is validation only, never a mutation of the stored/typed value, so
 * interior newlines and surrounding whitespace the user typed are preserved
 * for the caller to persist or re-render.
 *
 * `isValid` is judged against the *trimmed* text (a whitespace-only comment
 * is invalid) but the untrimmed length is what is checked against the
 * 2000-character ceiling, matching how the composer's `maxLength` attribute
 * counts characters as the user types.
 */
const validateCommentText = (text) => {
  const value = typeof text === "string" ? text : "";
  const trimmed = value.trim();

  if (trimmed.length < COMMENT_MIN_TRIMMED_LENGTH) {
    return { value, isValid: false, reason: "empty" };
  }

  if (value.length > COMMENT_MAX_LENGTH) {
    return { value, isValid: false, reason: "too_long" };
  }

  return { value, isValid: true, reason: null };
};

/**
 * `localStorage.getItem("user_id")` is never written for Admin accounts
 * (`setUserRoleFromCrewRoles` returns at its `roleId === 1` branch before
 * the key is set — TRD §1.1 finding 1, TR1), so this must never throw for
 * that population: absence, malformed JSON, and a missing `localStorage`
 * entirely all resolve to `null` rather than propagating an error.
 */
const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem("user_id");
    if (raw === null || raw === undefined) return null;
    const parsed = JSON.parse(raw);
    return parsed === null || parsed === undefined ? null : parsed;
  } catch (error) {
    return null;
  }
};

/**
 * Is `comment` authored by `currentUserId`? Prefers the server-derived
 * `is_own` boolean when present (D7, TR1) — the server already makes this
 * decision authoritatively for PUT/DELETE, so trusting it here keeps the
 * buttons correct for Admins, whose `currentUserId` is `null` (TR1).
 *
 * Falls back to a `Number(...)`-coerced identity comparison only when
 * `is_own` is not a boolean (e.g. an older cached response). Fails closed:
 * a `null`/`undefined` `currentUserId`, a `null` comment, or a
 * non-numeric id on either side returns `false` rather than granting
 * access.
 */
const isOwnComment = (comment, currentUserId) => {
  if (!comment) return false;

  if (typeof comment.is_own === "boolean") {
    return comment.is_own;
  }

  if (currentUserId === null || currentUserId === undefined) return false;

  const createdBy = Number(comment.created_by);
  const actor = Number(currentUserId);

  if (Number.isNaN(createdBy) || Number.isNaN(actor)) return false;

  return createdBy === actor;
};

/**
 * Prefers the server's own message (which survives `ErrorHandlerMiddleware`
 * for any deliberate `HttpError` under 500 — TRD §1.1 finding 3) and falls
 * back to `fallback` when the response carries no message, matching the
 * `error?.response?.data?.message || FALLBACK` pattern already used for
 * `StatusNoteRequiredError` in `UnitJob.js`/`MyProjectCheckList.js`.
 */
const commentErrorMessage = (error, fallback) => {
  return error?.response?.data?.message || fallback;
};

export {
  COMMENT_MAX_LENGTH,
  COMMENT_MIN_TRIMMED_LENGTH,
  validateCommentText,
  isOwnComment,
  getCurrentUserId,
  commentErrorMessage,
};
